import { BIndexThresholdType, QuestionStatus } from '@report/graphql';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDate,
  useQueryStates,
  parseAsStringEnum,
  parseAsString,
  parseAsFloat,
} from 'nuqs';

import { ItemAnalysisTab, type ItemAnalysisTabType } from '../../constants';

export function useItemAnalysisSearchParams() {
  const [
    {
      startDate,
      endDate,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
      page,
      pageSize,
      navigationTab,
      pBufferHardThreshold,
      pBufferMediumThreshold,
      pBufferEasyThreshold,
    },
    setQueryParams,
  ] = useQueryStates({
    startDate: parseAsIsoDate,
    endDate: parseAsIsoDate,
    examCourse: parseAsString.withDefault(''),
    questionStatus: parseAsArrayOf(parseAsStringEnum(Object.values(QuestionStatus))).withDefault(
      [],
    ),
    minExaminees: parseAsInteger,
    knowledgeGroupSelection: parseAsStringEnum(Object.values(BIndexThresholdType)).withDefault(
      BIndexThresholdType.UseExamResult,
    ),
    knowledgeGroupCustomThreshold: parseAsInteger,
    nonKnowledgeGroupSelection: parseAsStringEnum(Object.values(BIndexThresholdType)).withDefault(
      BIndexThresholdType.UseExamResult,
    ),
    nonKnowledgeGroupCustomThreshold: parseAsInteger,
    page: parseAsInteger.withDefault(0),
    pageSize: parseAsInteger.withDefault(50),
    navigationTab: parseAsStringEnum(Object.values(ItemAnalysisTab)).withDefault(
      ItemAnalysisTab.QBankOverview,
    ),
    pBufferHardThreshold: parseAsFloat.withDefault(0.4),
    pBufferMediumThreshold: parseAsFloat.withDefault(0.7),
    pBufferEasyThreshold: parseAsFloat.withDefault(1),
  });

  const changeFilterParams = (params: {
    startDate: Date | null;
    endDate: Date | null;
    examCourse: string;
    questionStatus: QuestionStatus[];
    minExaminees: number | null;
    knowledgeGroupSelection: BIndexThresholdType;
    knowledgeGroupCustomThreshold: number | null;
    nonKnowledgeGroupSelection: BIndexThresholdType;
    nonKnowledgeGroupCustomThreshold: number | null;
    page: number;
    pageSize: number;
  }) => {
    setQueryParams(params);
  };

  const changePage = (page: number) => {
    setQueryParams({ page });
  };
  const changePageSize = (pageSize: number) => {
    setQueryParams({ page: 0, pageSize });
  };

  const changeNavigationTab = (navigationTab: ItemAnalysisTabType) => {
    setQueryParams({ navigationTab, page: 0, pageSize: 50 });
  };

  const changePBufferThresholds = (params: {
    pBufferHardThreshold: number;
    pBufferMediumThreshold: number;
    pBufferEasyThreshold: number;
  }) => {
    setQueryParams({
      ...params,
    });
  };

  return [
    {
      startDate,
      endDate,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
      page,
      pageSize,
      navigationTab,
      pBufferHardThreshold,
      pBufferMediumThreshold,
      pBufferEasyThreshold,
    },
    {
      changeFilterParams,
      changePage,
      changePageSize,
      changeNavigationTab,
      changePBufferThresholds,
    },
  ] as const;
}
